/**
 * Credit Events System
 * 
 * Provides a centralized event system for credit-related changes
 * to enable real-time updates across all components.
 */

export interface CreditChangeEvent {
  type: 'credit_consumed' | 'credit_added' | 'subscription_changed' | 'credit_refunded';
  userId: string;
  amount: number;
  newBalance: number;
  timestamp: number;
  details?: {
    transactionId?: string;
    reason?: string;
    analysisId?: string;
    enhancementId?: string;
    originalCreativeId?: string;
    platform?: string;
  };
}

export interface CreditEventListener {
  (event: CreditChangeEvent): void;
}

class CreditEventManager {
  private listeners: Set<CreditEventListener> = new Set();
  private eventHistory: CreditChangeEvent[] = [];
  private readonly MAX_HISTORY_SIZE = 100;

  /**
   * Add a listener for credit change events
   */
  addListener(listener: CreditEventListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Broadcast a credit change event to all listeners
   */
  broadcast(event: CreditChangeEvent): void {
    console.log('📢 Broadcasting credit change event:', {
      type: event.type,
      userId: event.userId,
      amount: event.amount,
      newBalance: event.newBalance,
      listeners: this.listeners.size
    });

    // Add to history
    this.eventHistory.push(event);
    
    // Limit history size
    if (this.eventHistory.length > this.MAX_HISTORY_SIZE) {
      this.eventHistory = this.eventHistory.slice(-this.MAX_HISTORY_SIZE);
    }

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('❌ Error in credit event listener:', error);
      }
    });
  }

  /**
   * Get recent credit events for debugging
   */
  getRecentEvents(limit: number = 10): CreditChangeEvent[] {
    return this.eventHistory.slice(-limit).reverse();
  }

  /**
   * Get current listener count
   */
  getListenerCount(): number {
    return this.listeners.size;
  }

  /**
   * Clear all listeners (useful for testing)
   */
  clearListeners(): void {
    this.listeners.clear();
    console.log('🗑️ All credit event listeners cleared');
  }

  /**
   * Clear event history
   */
  clearHistory(): void {
    this.eventHistory = [];
    console.log('🗑️ Credit event history cleared');
  }
}

// Global instance
export const creditEventManager = new CreditEventManager();

/**
 * Helper function to create and broadcast credit change events
 */
export function broadcastCreditChange(
  type: CreditChangeEvent['type'],
  userId: string,
  amount: number,
  newBalance: number,
  details?: CreditChangeEvent['details']
): void {
  const event: CreditChangeEvent = {
    type,
    userId,
    amount,
    newBalance,
    timestamp: Date.now(),
    details
  };

  creditEventManager.broadcast(event);
}

/**
 * Helper function to subscribe to credit changes for a specific user
 */
export function subscribeToCreditChanges(
  userId: string,
  callback: (event: CreditChangeEvent) => void
): () => void {
  const listener = (event: CreditChangeEvent) => {
    if (event.userId === userId) {
      callback(event);
    }
  };

  return creditEventManager.addListener(listener);
}

/**
 * Helper function to subscribe to all credit changes
 */
export function subscribeToAllCreditChanges(
  callback: (event: CreditChangeEvent) => void
): () => void {
  return creditEventManager.addListener(callback);
}

/**
 * Get debug information about the credit event system
 */
export function getCreditEventDebugInfo(): {
  listenerCount: number;
  recentEvents: CreditChangeEvent[];
  systemActive: boolean;
} {
  return {
    listenerCount: creditEventManager.getListenerCount(),
    recentEvents: creditEventManager.getRecentEvents(5),
    systemActive: creditEventManager.getListenerCount() > 0
  };
}

export default creditEventManager;